import { FiAlertCircle, FiAlertTriangle, FiCheckCircle, FiInfo, FiX } from 'react-icons/fi';
import { Toast } from '@base-ui/react/toast';
import { notificationStyles } from '@/shared/ui/notification/styles';
import type { NotificationTone } from '@/shared/ui/notification/types';

/**
 * The tone icon.
 *
 * Feather only, per the icon rule. The icon is DECORATIVE — `aria-hidden` — and
 * the title carries the meaning. The icon exists so the tone survives for an
 * operator who cannot separate the colours, which is why it is a distinct glyph
 * per tone and not the same shape recoloured.
 */
const TONE_ICON = {
  success: FiCheckCircle,
  error: FiAlertCircle,
  warning: FiAlertTriangle,
  info: FiInfo,
} as const;

/**
 * One notification's contents.
 *
 * Rendered inside `Toast.Root`, which owns the transition and swipe behaviour.
 * This component owns only what the operator reads: the icon, the title, the
 * optional description, the optional action, and the dismiss control.
 *
 * The dismiss control is a real `<button>` with an accessible name — an icon
 * that IS the control needs its name on the button, not on the icon.
 */
export function NotificationCard({
  toast,
}: {
  toast: {
    type?: string | undefined;
    title?: React.ReactNode;
    description?: React.ReactNode;
    actionProps?: React.ComponentPropsWithoutRef<'button'> | undefined;
  };
}) {
  const tone = toneOf(toast.type);
  const Icon = TONE_ICON[tone];

  return (
    <>
      <Icon className={notificationStyles.icon({ tone })} aria-hidden="true" />

      <div className={notificationStyles.body}>
        <Toast.Title className={notificationStyles.title}>{toast.title}</Toast.Title>

        {toast.description === undefined ? null : (
          <Toast.Description className={notificationStyles.description}>
            {toast.description}
          </Toast.Description>
        )}

        {toast.actionProps === undefined ? null : (
          // `Toast.Action` already merges `toast.actionProps` into the button —
          // its label and its onClick. Spreading them here as well would apply
          // the handler twice, so only the styling is passed.
          <Toast.Action className={notificationStyles.action} />
        )}
      </div>

      <Toast.Close className={notificationStyles.close} aria-label="Dismiss notification">
        <FiX className="size-4" aria-hidden="true" />
      </Toast.Close>
    </>
  );
}

function toneOf(type: string | undefined): NotificationTone {
  if (type === 'success' || type === 'error' || type === 'warning' || type === 'info') {
    return type;
  }
  return 'info';
}
